import ModalCloseButton from '../../../Component/UI/ModalCloseButton';
import { FormEvent, useRef, useState } from "react";
import styled from "styled-components";
import api2 from "../../../Component/axios_Interceptor/api2";
import url from "../../../Component/server";
import { fontStyles } from "../../../Component/style/admin_font";

type RecoveryMode = "find_id" | "reset_pw";

interface Props {
  initialMode: RecoveryMode;
  onClose: () => void;
}

function messageFrom(error: unknown, fallback: string) {
  const response = (error as { response?: { data?: { message?: unknown } } }).response;
  const message = response?.data?.message;
  if (typeof message === "string") return message;
  if (Array.isArray(message) && message.every(value => typeof value === "string")) return message.join(" ");
  return fallback;
}

export default function SellerAccountRecovery({ initialMode, onClose }: Props) {
  const [mode, setMode] = useState<RecoveryMode>(initialMode);
  const [name, setName] = useState("");
  const [userid, setUserid] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [result, setResult] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);

  const cleanPhone = phone.replace(/[^0-9]/g, "");
  const run = async (work: () => Promise<void>) => {
    if (lock.current) return;
    lock.current = true;
    setBusy(true);
    setError("");
    try {
      await work();
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };

  const changeMode = (next: RecoveryMode) => {
    if (busy || next === mode) return;
    setMode(next);
    setCode("");
    setCodeSent(false);
    setVerified(false);
    setResult("");
    setError("");
  };

  const changePhone = (value: string) => {
    setPhone(value.replace(/[^0-9]/g, "").slice(0, 11));
    setCode("");
    setCodeSent(false);
    setVerified(false);
    setResult("");
  };

  const sendCode = () => run(async () => {
    if (!/^01[0-9]{8,9}$/.test(cleanPhone)) {
      setError("휴대폰 번호를 숫자만 입력해 주세요.");
      return;
    }
    try {
      await api2.post(url.Communication_url.send_phone_auth, { phone: cleanPhone, purpose: mode });
      setCodeSent(true);
      setVerified(false);
      setCode("");
      setResult("");
    } catch (requestError) {
      setError(messageFrom(requestError, "인증번호를 발송하지 못했습니다. 잠시 후 다시 시도해 주세요."));
    }
  });

  const verifyCode = () => run(async () => {
    if (!/^\d{6}$/.test(code)) {
      setError("카카오톡으로 받은 인증번호 6자리를 입력해 주세요.");
      return;
    }
    try {
      await api2.post(url.Communication_url.verify_phone_auth, { phone: cleanPhone, code, purpose: mode });
      setVerified(true);
      setResult("");
    } catch (requestError) {
      setError(messageFrom(requestError, "인증번호를 확인하지 못했습니다."));
    }
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    run(async () => {
      if (!verified) {
        setError("휴대폰 인증을 먼저 완료해 주세요.");
        return;
      }
      try {
        if (mode === "find_id") {
          if (!name.trim()) {
            setError("가입할 때 입력한 대표자명을 입력해 주세요.");
            return;
          }
          const response = await api2.post(url.Communication_url.seller_find_id, { name: name.trim(), phone: cleanPhone });
          setResult(`가입하신 셀러 아이디는 ${response.data.userid} 입니다.`);
          return;
        }
        if (!userid.trim()) {
          setError("셀러 아이디를 입력해 주세요.");
          return;
        }
        if (!/^[!-~]{5,72}$/.test(newPassword)) {
          setError("새 비밀번호는 공백 없이 5~72자로 입력해 주세요.");
          return;
        }
        if (newPassword !== passwordConfirm) {
          setError("새 비밀번호 확인이 일치하지 않습니다.");
          return;
        }
        await api2.post(url.Communication_url.seller_reset_password, {
          userid: userid.trim(), phone: cleanPhone, new_password: newPassword,
        });
        setResult("비밀번호가 변경되었습니다. 새 비밀번호로 로그인해 주세요.");
        setNewPassword("");
        setPasswordConfirm("");
      } catch (requestError) {
        setError(messageFrom(requestError, mode === "find_id" ? "셀러 아이디를 찾지 못했습니다." : "비밀번호를 변경하지 못했습니다."));
      }
    });
  };

  return (
    <Backdrop data-admin-modal-overlay="" onMouseDown={event => { if (event.target === event.currentTarget && !busy) onClose(); }}>
      <Dialog data-admin-modal="" role="dialog" aria-modal="true" aria-labelledby="seller-recovery-title">
        <Heading data-admin-modal-header="">
          <div>
            <h2 id="seller-recovery-title">계정 정보 찾기</h2>
            <p>가입할 때 등록한 휴대폰의 카카오톡으로 인증번호를 보내드립니다.</p>
          </div>
          <CloseButton tone="admin" type="button" onClick={onClose} disabled={busy} aria-label="계정 정보 찾기 닫기" />
        </Heading>
        <Tabs data-admin-tabs="" aria-label="계정 찾기 방식">
          <button type="button" className={mode === "find_id" ? "active" : ""} aria-pressed={mode === "find_id"} onClick={() => changeMode("find_id")}>아이디 찾기</button>
          <button type="button" className={mode === "reset_pw" ? "active" : ""} aria-pressed={mode === "reset_pw"} onClick={() => changeMode("reset_pw")}>비밀번호 재설정</button>
        </Tabs>
        <form data-admin-modal-content="" onSubmit={submit}><div data-admin-modal-body="">
          {mode === "find_id" ? (
            <Field><span>대표자명</span><input value={name} onChange={event => { setName(event.target.value); setResult(""); }} autoComplete="name" disabled={busy} required /></Field>
          ) : (
            <Field><span>셀러 아이디</span><input value={userid} onChange={event => { setUserid(event.target.value); setResult(""); }} autoComplete="username" disabled={busy} required /></Field>
          )}
          <Field><span>휴대폰 번호</span><Inline><input value={phone} onChange={event => changePhone(event.target.value)} inputMode="numeric" autoComplete="tel" placeholder="숫자만 입력" disabled={busy} required /><button type="button" onClick={sendCode} disabled={busy}>{codeSent ? "다시 받기" : "인증번호 받기"}</button></Inline></Field>
          {codeSent && <Field><span>인증번호</span><Inline><input value={code} onChange={event => setCode(event.target.value.replace(/[^0-9]/g, "").slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" placeholder="6자리" disabled={busy || verified} required /><button type="button" onClick={verifyCode} disabled={busy || verified}>{verified ? "인증 완료" : "인증하기"}</button></Inline></Field>}
          {mode === "reset_pw" && verified && <><Field><span>새 비밀번호</span><input type="password" value={newPassword} onChange={event => setNewPassword(event.target.value)} autoComplete="new-password" minLength={5} maxLength={72} disabled={busy} required /><small>공백 없이 5~72자</small></Field><Field><span>새 비밀번호 확인</span><input type="password" value={passwordConfirm} onChange={event => setPasswordConfirm(event.target.value)} autoComplete="new-password" minLength={5} maxLength={72} disabled={busy} required /></Field></>}
          {error && <Feedback role="alert" $error>{error}</Feedback>}
          {result && <Feedback role="status">{result}</Feedback>}
          </div><div data-admin-modal-footer=""><SubmitButton type="submit" disabled={busy || !verified}>{busy ? "처리 중" : mode === "find_id" ? "아이디 확인" : "비밀번호 변경"}</SubmitButton></div>
        </form>
      </Dialog>
    </Backdrop>
  );
}

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 1000; display: flex; align-items: center; justify-content: center;
  padding: 20px; background: rgba(24, 18, 36, 0.55);
`;
const Dialog = styled.div`
  box-sizing: border-box; width: 100%; max-width: 520px; max-height: calc(100dvh - 40px); overflow-y: auto;
  padding: 28px; border-radius: 12px; background: var(--user-surface); color: var(--user-text);
  @media (max-width: 520px) { padding: 22px 18px; }
`;
const Heading = styled.div`
  display: flex; align-items: flex-start; justify-content: space-between; gap: 16px;
  h2 { margin: 0; font-size: 23px; ${fontStyles.medium} }
  p { margin: 8px 0 0; color: var(--user-text-muted); font-size: 14px; line-height: 1.55; }
`;
const CloseButton = styled(ModalCloseButton)``;
const Tabs = styled.div.attrs<{ 'data-admin-tabs'?: string }>({ 'data-admin-tabs': '' })`
  display: grid; grid-template-columns: 1fr 1fr; gap: 4px; margin: 24px 0; padding: 4px; border-radius: 8px; background: var(--user-surface-raised);
  button { min-height: 44px; border: 0; border-radius: 6px; background: transparent; color: var(--user-text-muted); cursor: pointer; ${fontStyles.medium} }
  button.active { background: var(--user-surface); color: var(--user-accent); box-shadow: 0 1px 5px rgba(45, 30, 75, 0.12); }
`;
const Field = styled.label`
  display: block; margin-top: 18px;
  > span { display: block; margin-bottom: 8px; font-size: 14px; ${fontStyles.medium} }
  input { box-sizing: border-box; width: 100%; min-width: 0; min-height: 48px; padding: 11px 12px; border: 1px solid var(--user-border); border-radius: 8px; font-size: 16px; outline: none; background: var(--user-surface); color: var(--user-text); }
  input::placeholder { color: var(--user-text-muted); }
  input:focus { border-color: var(--user-accent); box-shadow: 0 0 0 3px rgba(96, 65, 170, 0.12); }
  small { display: block; margin-top: 7px; color: var(--user-text-muted); font-size: 12px; line-height: 1.5; }
`;
const Inline = styled.div`
  display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 8px;
  button { min-width: 112px; min-height: 48px; padding: 0 12px; border: 1px solid var(--user-accent); border-radius: 8px; background: var(--user-surface); color: var(--user-accent); cursor: pointer; ${fontStyles.medium} }
  button:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const Feedback = styled.p<{ $error?: boolean }>`
  margin: 18px 0 0; padding: 11px 12px; border-radius: 7px; background: ${props => props.$error ? "var(--user-error-soft)" : "var(--user-success-soft)"};
  color: ${props => props.$error ? "var(--user-error)" : "var(--user-success)"}; font-size: 14px; line-height: 1.55;
`;
const SubmitButton = styled.button`
  width: 100%; min-height: 50px; margin-top: 22px; border: 0; border-radius: 8px; background: #4f319b; color: white; font-size: 16px; cursor: pointer; ${fontStyles.medium}
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;
